import { useState } from "react";
import { Parser } from "json2csv";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import Modal from "@components/layout/Modal";
import { auditReportExport, ReportColumn } from "@services/reportEngine";
import { formatDate } from "@utils/formatDate";
import { toastError } from "@utils/toast";
import { formatCell, formatCsvValue } from "./formatCell";

const escapeHtml = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

type ExportFormat = "csv" | "pdf";

const PDF_COLUMNS_PER_PAGE = 8;

/** Split wide tables horizontally and repeat the first identity column. */
const pdfColumnPages = (columns: ReportColumn[]): ReportColumn[][] => {
  if (columns.length <= PDF_COLUMNS_PER_PAGE) return [columns];
  const [identity, ...remaining] = columns;
  const pages: ReportColumn[][] = [
    [identity, ...remaining.slice(0, PDF_COLUMNS_PER_PAGE - 1)],
  ];
  for (
    let index = PDF_COLUMNS_PER_PAGE - 1;
    index < remaining.length;
    index += PDF_COLUMNS_PER_PAGE - 1
  ) {
    pages.push([
      identity,
      ...remaining.slice(index, index + PDF_COLUMNS_PER_PAGE - 1),
    ]);
  }
  return pages;
};

export const buildReportPdfHtml = ({
  title,
  date,
  columns,
  rows,
  label,
}: {
  title: string;
  date: string;
  columns: ReportColumn[];
  rows: Record<string, any>[];
  label: (column: ReportColumn) => string;
}) => `
  <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; margin: 10px; }
        table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        th, td { border: 1px solid #ddd; padding: 4px; text-align: left; font-size: 8px; overflow-wrap: anywhere; }
        th { background-color: #f2f2f2; font-weight: bold; }
        h2 { color: #333; margin-bottom: 10px; font-size: 16px; }
        .summary { margin-bottom: 10px; font-size: 10px; color: #666; }
        .report-table + .report-table { break-before: page; page-break-before: always; }
      </style>
    </head>
    <body>
      <h2>${escapeHtml(title)}</h2>
      <div class="summary"><p>${escapeHtml(date)}</p><p>${
  rows.length
} rows</p></div>
      ${pdfColumnPages(columns)
        .map(
          pageColumns => `<div class="report-table"><table>
            <thead><tr>${pageColumns
              .map(column => `<th>${escapeHtml(label(column))}</th>`)
              .join("")}</tr></thead>
            <tbody>${rows
              .map(
                row =>
                  `<tr>${pageColumns
                    .map(
                      column =>
                        `<td>${escapeHtml(
                          formatCell(
                            row[column.name],
                            column.dataType,
                            column.name,
                          ),
                        )}</td>`,
                    )
                    .join("")}</tr>`,
              )
              .join("")}</tbody>
          </table></div>`,
        )
        .join("")}
    </body>
  </html>`;

export const buildReportCsv = ({
  columns,
  rows,
  label,
}: {
  columns: ReportColumn[];
  rows: Record<string, any>[];
  label: (column: ReportColumn) => string;
}): string =>
  new Parser({
    // Keep field identity separate from its display heading. Two valid fields
    // may share a heading (for example entitlement Zone and offtake Zone), and
    // building an object keyed by the heading silently drops one of them.
    fields: columns.map(column => ({
      label: label(column),
      value: (row: Record<string, any>) =>
        formatCsvValue(row[column.name], column.dataType, column.name),
    })),
  }).parse(rows);

/**
 * Saved-report names use the application's readable dd/MM/yyyy dates. A slash
 * is a path separator in downloaded/evidence filenames, so passing that name
 * through unchanged can reduce the download to only its final year. Keep the
 * display name, but make the filesystem name portable and predictable.
 */
export const sanitizeExportBaseName = (value: string): string => {
  const maximumLength = 220;
  const withoutControlCharacters = Array.from(value)
    .filter(character => character >= " " && character !== "\u007f")
    .join("");
  const safe = withoutControlCharacters
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/^[.\s-]+|[.\s-]+$/g, "")
    .trim();

  if (!safe) return "Report";
  if (safe.length <= maximumLength) return safe;

  // Suggested saved-report names end with a complete dd-MM-yyyy date range.
  // Keep that evidence-defining suffix when a long report/scheme name needs
  // truncation; a simple prefix slice can silently reduce it to only a year.
  const dateRange = safe.match(/\d{2}-\d{2}-\d{4}[–-]\d{2}-\d{2}-\d{4}$/)?.[0];
  if (dateRange) {
    const separator = " … ";
    const prefixLength = maximumLength - separator.length - dateRange.length;
    return `${safe.slice(0, prefixLength).trimEnd()}${separator}${dateRange}`;
  }

  return safe.slice(0, maximumLength).trim();
};

/**
 * The DEQ export dialog and its export, shared by the run step and the
 * snapshot view: selected rows, or the whole report when none are selected,
 * as .csv or .pdf. The browser creates the file and the API records bounded audit
 * metadata, following the DEQ lifecycle.
 */
export const useExportReportRows = ({
  baseFileName,
  columns,
  rows,
  selectedRows,
  resultToken,
  reportSnapshotId,
}: {
  baseFileName: string;
  columns: ReportColumn[];
  rows: Record<string, any>[];
  selectedRows: Record<string, any>[];
  resultToken?: string;
  reportSnapshotId?: string;
}) => {
  const { t } = useTranslation();
  const hasSelection = selectedRows.length > 0;
  const exportRowsData = hasSelection ? selectedRows : rows;
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportFormat>("csv");
  const columnLabel = (column: ReportColumn): string =>
    (column.displayLabel ?? t(column.label)) as string;

  const { mutateAsync: exportRows, isLoading: isExporting } = useMutation(
    async (format: ExportFormat) => {
      if (Boolean(resultToken) === Boolean(reportSnapshotId)) {
        throw new Error("one report provenance source is required");
      }

      const exportBaseFileName = sanitizeExportBaseName(baseFileName);
      let file: File;
      if (format === "csv") {
        const csv = buildReportCsv({
          columns,
          rows: exportRowsData,
          label: columnLabel,
        });
        file = new File([csv], `${exportBaseFileName}.csv`, {
          type: "text/csv",
        });
      } else {
        const tableHtml = buildReportPdfHtml({
          title: baseFileName,
          date: formatDate(new Date()),
          columns,
          rows: exportRowsData,
          label: columnLabel,
        });
        const { default: html2pdf } = await import("html2pdf.js");
        const pdfBlob = await html2pdf()
          .from(tableHtml)
          .set({
            margin: [5, 5, 5, 5],
            image: { type: "jpeg", quality: 0.95 },
            html2canvas: { scale: 1, width: 1200 },
            jsPDF: {
              unit: "mm",
              format: "a4",
              orientation: "landscape",
              compress: true,
            },
          })
          .outputPdf("blob");
        file = new File([pdfBlob], `${exportBaseFileName}.pdf`, {
          type: "application/pdf",
        });
      }

      await auditReportExport({
        format,
        resultToken,
        reportSnapshotId,
        selectedRowCount: exportRowsData.length,
      });
      const downloadUrl = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    },
    {
      onError: (error: any) =>
        toastError(
          error?.response?.data?.message ?? t("reporting.export_failed"),
        ),
    },
  );

  const exportDialog = (
    <Modal open={exportModalOpen}>
      <div className="flex flex-col">
        <div className="border-b p-4">
          <h3 className="text-lg font-medium">{t("reporting.export_data")}</h3>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-500">
            {t(
              hasSelection
                ? "reporting.export_choose_format"
                : "reporting.export_all_choose_format",
              { count: exportRowsData.length },
            )}
          </p>
          {(["csv", "pdf"] as const).map(format => (
            <label
              key={format}
              className="flex items-center gap-2 text-sm"
              htmlFor={`export-${format}`}
            >
              <input
                id={`export-${format}`}
                type="radio"
                name="export-format"
                checked={exportType === format}
                onChange={() => setExportType(format)}
              />
              .{format}
            </label>
          ))}
        </div>
        <div className="border-t px-4 py-2 flex flex-row-reverse gap-3">
          <button
            type="button"
            className="btn-primary"
            disabled={isExporting}
            onClick={() => {
              // Closed only on success; a failed export keeps the dialog and
              // its toast so the operator can retry. The rejection is already
              // reported by the mutation's onError.
              exportRows(exportType)
                .then(() => setExportModalOpen(false))
                .catch(() => {});
            }}
          >
            {isExporting ? t("common.loading") : t("common.export")}
          </button>
          <button
            type="button"
            className="btn-default"
            disabled={isExporting}
            onClick={() => setExportModalOpen(false)}
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </Modal>
  );

  return {
    exportDialog,
    openExportDialog: () => setExportModalOpen(true),
    isExporting,
  };
};
